/**
 * Evaluation Details Page
 * Displays detailed information about an evaluation including runs and results
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';
import { useEvaluation } from '../../hooks/useEvaluation';
import { ROUTE_PATHS } from '../../constants/routePaths';

interface EvaluationRun {
  run_id: number;
  deployment_name: string;
  prompt_version: string;
  status: string;
  overall_score: number;
  total_test_cases: number;
  started_at: string;
  completed_at: string;
}

interface EvaluationDetail {
  evaluation_id: number;
  name: string;
  dataset_name: string;
  judge_model: string;
  status: string;
  overall_score?: number;
  created_at: string;
  completed_at?: string;
  failure_reason?: string | null;
  runs: EvaluationRun[];
}

export function EvaluationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    loading,
    getEvaluationById,
    getEvaluationRunResults,
  } = useEvaluation();

  const [evaluation, setEvaluation] = useState<EvaluationDetail | null>(null);
  const [runResults, setRunResults] = useState<any>(null);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      loadEvaluationDetails();
    }
  }, [id]);

  const loadEvaluationDetails = async () => {
    if (!id) return;
    try {
      const data = await getEvaluationById(id);
      setEvaluation(data);
    } catch (error) {
      console.error('Failed to load evaluation details:', error);
      toast.error('Failed to load evaluation details');
    }
  };

  const handleViewResults = async (runId: number) => {
    try {
      setSelectedRunId(runId);
      setResultsDialogOpen(true);
      const data = await getEvaluationRunResults(runId.toString());
      setRunResults(data);
    } catch (error) {
      console.error('Failed to load run results:', error);
      toast.error('Failed to load run results');
    }
  };

  const handleBack = () => {
    navigate(ROUTE_PATHS.EVALUATION);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
      case 'success':
        return { bgcolor: '#dcfce7', color: '#166534', borderColor: '#86efac' };
      case 'failure':
      case 'failed':
        return { bgcolor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' };
      case 'pending':
        return { bgcolor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' };
      case 'running':
      case 'processing':
        return { bgcolor: '#eff6ff', color: '#1e40af', borderColor: '#bfdbfe' };
      default:
        return { bgcolor: '#f1f5f9', color: '#475569', borderColor: '#cbd5e1' };
    }
  };

  if (loading && !evaluation) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!evaluation) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Evaluation not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={handleBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {evaluation.name}
            </Typography>
          </Box>
          {/* <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadEvaluationDetails}
              sx={{
                borderColor: '#cbd5e1',
                color: '#475569',
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: '#f8fafc',
                },
              }}
            >
              Refresh
            </Button>
          </Box> */}
        </Box>

        {/* Overview Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 600 }}>
                  Dataset
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {evaluation.dataset_name || 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 600 }}>
                  Status
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 2,
                    py: 0.75,
                    borderRadius: 2,
                    ...getStatusColor(evaluation.status),
                    border: `1px solid ${getStatusColor(evaluation.status).borderColor}`,
                  }}
                >
                  {evaluation.status.toLowerCase() === 'success' ? (
                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                  ) : evaluation.status.toLowerCase() === 'failure' || evaluation.status.toLowerCase() === 'failed' ? (
                    <ErrorIcon sx={{ fontSize: 18 }} />
                  ) : null}
                  <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                    {evaluation.status}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 600 }}>
                  Overall Score
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {evaluation.overall_score !== null && evaluation.overall_score !== undefined
                    ? (evaluation.overall_score * 100).toFixed(1) + '%'
                    : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 600 }}>
                  Judge Model
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                  {evaluation.judge_model || 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1, fontWeight: 600 }}>
                  Total Runs
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {evaluation.runs?.length || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Evaluation Details */}
        <Paper sx={{ mb: 3, p: 3, borderRadius: 3, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
            Evaluation Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                Created At
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDate(evaluation.created_at)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                Completed At
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {formatDate(evaluation.completed_at)}
              </Typography>
            </Grid>
            {evaluation.failure_reason && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                  Failure Reason
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#fee2e2',
                    borderRadius: 2,
                    border: '1px solid #fecaca',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 500 }}>
                    {evaluation.failure_reason}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Runs Table */}
        <Paper sx={{ borderRadius: 3, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Evaluation Runs
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
              {evaluation.runs?.length || 0} run(s) executed
            </Typography>
          </Box>
          
          {evaluation.runs && evaluation.runs.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Run ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Deployment</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Prompt Version</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">Overall Score</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">Test Cases</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Started At</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Completed At</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', width: 100 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {evaluation.runs.map((run) => (
                    <TableRow
                      key={run.run_id}
                      sx={{
                        '&:hover': { bgcolor: '#f8fafc' },
                        '&:last-child td': { border: 0 },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          #{run.run_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          {run.deployment_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {run.prompt_version}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            ...getStatusColor(run.status),
                            border: `1px solid ${getStatusColor(run.status).borderColor}`,
                          }}
                        >
                          {run.status.toLowerCase() === 'success' ? (
                            <CheckCircleIcon sx={{ fontSize: 14 }} />
                          ) : run.status.toLowerCase() === 'failure' || run.status.toLowerCase() === 'failed' ? (
                            <ErrorIcon sx={{ fontSize: 14 }} />
                          ) : null}
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              textTransform: 'uppercase',
                            }}
                          >
                            {run.status}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          {run.overall_score !== null && run.overall_score !== undefined
                            ? (run.overall_score * 100).toFixed(1) + '%'
                            : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          {run.total_test_cases}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {formatDate(run.started_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {formatDate(run.completed_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewResults(run.run_id)}
                          sx={{
                            color: '#2563eb',
                            '&:hover': { bgcolor: '#eff6ff' },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: '#94a3b8', mb: 1 }}>
                No runs found
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                Evaluation has not been executed yet
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Run Results Dialog */}
      <Dialog
        open={resultsDialogOpen}
        onClose={() => {
          setResultsDialogOpen(false);
          setRunResults(null);
          setSelectedRunId(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #e2e8f0' }}>
          Run Results - #{selectedRunId}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {loading && !runResults ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : runResults ? (
            <Paper
              sx={{
                p: 2,
                bgcolor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                maxHeight: '500px',
                overflow: 'auto',
              }}
            >
              <pre style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5 }}>
                {JSON.stringify(runResults, null, 2)}
              </pre>
            </Paper>
          ) : (
            <Typography color="text.secondary">No results available</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => {
              setResultsDialogOpen(false);
              setRunResults(null);
              setSelectedRunId(null);
            }}
            sx={{ color: '#64748b' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
